import { apiGet, apiGetResponse, apiPost } from "../../client";
import { ApiConfig } from "../../config";

const MEDICATIONS_PATH = `${ApiConfig.CLINIC_BASE}/medications/`;
const MEDICATION_INSTRUCTIONS_PATH = `${ApiConfig.CLINIC_BASE}/medication-instructions/`;
const PACKAGES_PATH = `${ApiConfig.CLINIC_BASE}/packages/`;
const INVENTORY_MEDICATIONS_PATH = `${ApiConfig.INVENTORY_BASE}/medications/`;
const INVENTORY_CHECK_STOCK_ITEMS_PATH = `${ApiConfig.INVENTORY_BASE}/check-stock/items/`;
const TMT_MEDICATIONS_SUGGESTIONS_PATH = `${ApiConfig.CLINIC_BASE}/tmt-medications/suggestions/`;

export const SYSTEM_MEDICATION_UNITS = [
  "tablet",
  "capsule",
  "blister",
  "bottle",
  "box",
  "pack",
  "sachet",
  "tube",
  "set",
  "nos",
  "เม็ด",
  "แคปซูล",
  "แผง",
  "ขวด",
  "กล่อง",
  "แพ็ค",
  "ซอง",
  "หลอด",
  "ชุด",
  "ชิ้น",
];

export const SYSTEM_UNIT_OPTIONS = SYSTEM_MEDICATION_UNITS.map((unit) => [unit, unit]);

async function fetchAllPaginatedResults(fetchPage, options = {}) {
  const firstPage = await fetchPage({ ...options, page: 1 });
  const firstResults = Array.isArray(firstPage?.results)
    ? firstPage.results
    : [];
  const totalPages = Number.parseInt(firstPage?.total_pages ?? 1, 10);

  if (!Number.isFinite(totalPages) || totalPages <= 1) {
    return firstResults;
  }

  const remainingPagePromises = [];
  for (let page = 2; page <= totalPages; page += 1) {
    remainingPagePromises.push(fetchPage({ ...options, page }));
  }

  const remainingPages = await Promise.all(remainingPagePromises);
  return remainingPages.reduce((allResults, pageData) => {
    const pageResults = Array.isArray(pageData?.results)
      ? pageData.results
      : [];
    return [...allResults, ...pageResults];
  }, firstResults);
}

/**
 * Fetches a medication list filtered by name. Returns default list for blank queries.
 */
export async function searchMedications(
  query,
  { page = 1, pageSize = 10, ordering = "prescription_count" } = {},
) {
  const trimmed = (query ?? "").trim();
  const params = new URLSearchParams();
  if (trimmed) {
    params.append("name", trimmed);
  }
  params.append("page", String(page));
  params.append("page_size", String(pageSize));
  if (ordering) {
    params.append("ordering", ordering);
  }
  const queryString = params.toString();
  return apiGet(
    queryString ? `${MEDICATIONS_PATH}?${queryString}` : MEDICATIONS_PATH,
  );
}

/**
 * Fetches all medications from all pages.
 */
export async function searchAllMedications(query) {
  const trimmed = (query ?? "").trim();
  const params = new URLSearchParams();
  if (trimmed) {
    params.append("name", trimmed);
  }
  params.append("ordering", "prescription_count");
  const queryString = params.toString();
  return fetchAllPaginatedResults(async (options = {}) => {
    const queryParams = new URLSearchParams(options);
    const allParams = new URLSearchParams(queryString);
    queryParams.forEach((value, key) => {
      if (key !== "name" && key !== "ordering") {
        allParams.append(key, value);
      }
    });
    return apiGet(`${MEDICATIONS_PATH}?${allParams.toString()}`);
  });
}

/**
 * Fetches package suggestions for payment and visiting management.
 */
export async function searchPackages(query = "", { page = 1, pageSize = 10, category = "", ordering = "", treatmentId, includeOutOfStock = false } = {}) {
  const trimmed = String(query ?? "").trim();
  const params = new URLSearchParams();
  if (trimmed) {
    params.append("name", trimmed);
  }
  if (category) {
    params.append("category", category);
  }
  if (ordering) {
    params.append("ordering", ordering);
  }
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  if (Number.isFinite(numericTreatmentId)) {
    params.append("treatment_id", String(numericTreatmentId));
  }
  if (includeOutOfStock) {
    params.append("include_out_of_stock", "true");
  }
  params.append("page", String(page));
  params.append("page_size", String(pageSize));
  const queryString = params.toString();
  return apiGet(
    queryString ? `${PACKAGES_PATH}?${queryString}` : PACKAGES_PATH,
  );
}

/**
 * Fetches list of package categories.
 */
export async function getPackageCategories() {
  return apiGet(`${PACKAGES_PATH}categories/`);
}

/**
 * Fetches package detail by id.
 */
export async function getPackageDetail(packageId, { treatmentId } = {}) {
  const numericId = Number.parseInt(packageId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสแพ็กเกจไม่ถูกต้อง");
  }
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  const query = Number.isFinite(numericTreatmentId)
    ? `?treatment_id=${numericTreatmentId}`
    : "";
  return apiGet(`${PACKAGES_PATH}${numericId}/${query}`);
}

/**
 * Fetches medication instruction suggestions based on a search string.
 */
export async function searchMedicationInstructions(query, { page = 1, pageSize = 5 } = {}) {
  const trimmed = (query ?? "").trim();
  if (!trimmed) {
    return { results: [] };
  }

  const params = new URLSearchParams({ q: trimmed });
  params.append("page", String(page));
  params.append("page_size", String(pageSize));
  return apiGet(`${MEDICATION_INSTRUCTIONS_PATH}?${params.toString()}`);
}

/**
 * Fetches ranked medication suggestions from the central TMT dataset based on a search string.
 */
export async function searchTMTMedicationSuggestions(query, { limit = 10 } = {}) {
  const trimmed = (query ?? "").trim();
  if (!trimmed) {
    return { results: [] };
  }

  const params = new URLSearchParams({ q: trimmed });
  if (limit !== undefined && limit !== null) {
    params.append("limit", String(limit));
  }
  return apiGet(`${TMT_MEDICATIONS_SUGGESTIONS_PATH}?${params.toString()}`);
}

/**
 * Fetches list of medical certificate.
 */
export async function fetchMedicalCertificates() {
  return apiGet(`${ApiConfig.INVENTORY_BASE}/medical-certificates/`);
}

/**
 * Fetches a page of medications from inventory API.
 */
export async function fetchInventoryMedications({
  page = 1,
  pageSize = 20,
  search = "",
} = {}) {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("page_size", String(pageSize));

  const trimmed = String(search ?? "").trim();
  if (trimmed) {
    params.append("search", trimmed);
  }

  return apiGet(`${INVENTORY_MEDICATIONS_PATH}?${params.toString()}`);
}

/**
 * Fetches one medication detail from inventory API.
 */
export async function fetchInventoryMedicationDetail(medicationId) {
  const numericId = Number.parseInt(medicationId, 10);
  if (!Number.isFinite(numericId)) {
    throw new Error("รหัสยาไม่ถูกต้อง");
  }

  return apiGet(`${INVENTORY_MEDICATIONS_PATH}${numericId}`);
}

/**
 * Runs ERP sync command for inventory medications.
 */
export async function syncInventoryMedicationsFromErp() {
  return apiPost(`${INVENTORY_MEDICATIONS_PATH}sync-erp/`);
}

/**
 * Fetches the latest ERP sync status from inventory API.
 */
export async function fetchInventorySyncStatus() {
  return apiGet(`${INVENTORY_MEDICATIONS_PATH}sync-erp/`);
}

/**
 * Fetches ordered items for stock checking on a target date.
 */
export async function fetchInventoryCheckStockItems({ date } = {}) {
  const params = new URLSearchParams();
  const trimmedDate = String(date ?? "").trim();

  if (trimmedDate) {
    params.append("date", trimmedDate);
  }

  const queryString = params.toString();
  return apiGet(
    queryString
      ? `${INVENTORY_CHECK_STOCK_ITEMS_PATH}?${queryString}`
      : INVENTORY_CHECK_STOCK_ITEMS_PATH,
  );
}

/**
 * Fetches every medication page from inventory API.
 */
export async function fetchAllInventoryMedications(options = {}) {
  return fetchAllPaginatedResults(fetchInventoryMedications, options);
}

/**
 * Fetches a medical certificate PDF from the API.
 */
export async function showMedicalCertificate(certificateId, language = "th") {
  return apiGetResponse(
    `/v1/documents/medical-certificates/templates/?medical_certificate_id=${certificateId}&language=${language}`,
  );
}

/**
 * Fetches a medical certificate PDF template file directly from the API.
 */
export async function showMedicalCertificateTemplateFile(
  certificateId,
  language = "th",
) {
  return apiGetResponse(
    `/v1/documents/medical-certificates/template-file/?medical_certificate_id=${certificateId}&language=${language}`,
  );
}

/**
 * Fetches a medical certificate PDF from the API.
 */
export async function showRenderMedicalCertificate(treatmentCertificateIdList) {
  const ids = (treatmentCertificateIdList || []).filter(
    (id) => id !== null && id !== undefined && String(id).trim() !== "",
  );

  if (ids.length === 0) {
    throw new Error("No valid certificate IDs provided for rendering");
  }

  const params = new URLSearchParams();
  ids.forEach((id) =>
    params.append("treatment_medical_certificate_ids", String(id)),
  );

  return apiGetResponse(
    `/v1/documents/medical-certificates/render/?${params.toString()}`,
  );
}

/**
 * Fetches a medicine sticker PDF from the API.
 * @param {string|number} medicationDispenseItemId - medication dispense item ID
 * @param {string} [language] - label language: "th" (default), "en", "mm", or "ls"
 */
export async function showMedicineSticker(medicationDispenseItemId, language = "th") {
  const params = new URLSearchParams({
    medication_dispense_item_id: medicationDispenseItemId,
    language,
  });
  return apiGetResponse(
    `/v1/documents/medicine-stickers/render/?${params.toString()}`,
  );
}

/**
 * Sends a medicine sticker print request through backend broadcast printing.
 * @param {string|number} medicationDispenseItemId - medication dispense item ID
 * @param {string} [language] - label language: "th" (default), "en", "mm", or "ls"
 */
export async function printMedicineSticker(medicationDispenseItemId, language = "th") {
  return apiPost(
    "/v1/documents/medicine-stickers/print/",
    {
      medication_dispense_item_id: medicationDispenseItemId,
      language,
    },
  );
}

/**
 * Fetches a blank medicine sticker PDF template from the API.
 */
export async function showMedicineStickerTemplate() {
  return showMedicineStickerTemplateWithPayload({});
}

/**
 * Fetches a medicine sticker PDF template from the API using the provided fill values.
 */
export async function showMedicineStickerTemplateWithPayload(payload = {}) {
  const params = new URLSearchParams();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    params.append(key, String(value));
  });

  const query = params.toString();
  return apiGetResponse(
    `/v1/documents/medicine-stickers/template/${query ? `?${query}` : ""}`,
  );
}

/**
 * Requests a translated medication content from the API.
 * @param {string} sourceLanguage - the source language for lookup
 * @param {string} language - the target language for translation ("en" for English, "th" for Thai, "mm" for Burmese, "ls" for Lao)
 * @param {string} contentValue - the value of the medication content to be translated
 * @param {string} [category] - the category of the medication content to be translated
 * @returns {Promise<Response>} - the response from the API
 */
export async function getTranslatedMedication(
  sourceLanguage,
  targetLanguage,
  contentValue,
  category,
) {
  const payload = {
    source_language: sourceLanguage,
    language: targetLanguage,
    content_value: contentValue,
    category: category,
  };

  return apiPost(
    `${ApiConfig.CLINIC_BASE}/medication-translations/translate/`,
    payload,
  );
}

/**
 * Fetches the list of available translation language choices from the API.
 * @returns {Promise<Array<{value: string, label: string}>>}
 */
export async function getTranslationLanguages() {
  return apiGet(`${ApiConfig.CLINIC_BASE}/medication-translations/languages/`);
}

/**
 * Fetches the list of preferred language choices for patients (excluding Thai).
 * @returns {Promise<Array<{value: string, label: string}>>}
 */
export async function getPreferenceLanguages() {
  return apiGet(`${ApiConfig.CLINIC_BASE}/preference-languages/`);
}
